'use client';

import { useState, useEffect, useCallback } from 'react';
import { listChats } from '../../lib/api/meridian-api-client.js';
import type { ChatSummary } from '../../types/index.js';

export interface UseSimpleChatsQueryOptions {
  apiBaseUrl?: string;
  status?: 'active' | 'archived' | 'all';
  limit?: number;
  enabled?: boolean;
}

export interface UseSimpleChatsQueryResult {
  data: ChatSummary[];
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

/**
 * Simple hook for fetching chats without TanStack Query overhead
 * 
 * Features:
 * - Direct API calls with minimal state management
 * - Manual refetch control
 * - No automatic retries or complex caching
 * - Lightweight and easy to understand
 */
export function useSimpleChatsQuery(options: UseSimpleChatsQueryOptions = {}): UseSimpleChatsQueryResult {
  const {
    apiBaseUrl = '/api/meridian',
    status = 'all',
    limit = 10,
    enabled = true,
  } = options;

  const [data, setData] = useState<ChatSummary[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchChats = useCallback(async () => {
    if (!enabled) return;

    setIsLoading(true);
    setIsError(false);
    setError(null);

    try {
      const response = await listChats(apiBaseUrl, {
        status,
        limit,
      });

      if (!response.success) {
        throw new Error(response.error || 'Failed to fetch chats');
      }

      setData(response.data?.chats || []);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Unknown error');
      setError(error);
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  }, [apiBaseUrl, status, limit, enabled]);

  // Fetch on mount and when dependencies change
  useEffect(() => {
    fetchChats();
  }, [fetchChats]);

  return {
    data,
    isLoading,
    isError,
    error,
    refetch: fetchChats,
  };
}
